import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface CollectionCount {
  collection: string;
  count: number;
  queryField: string;
}

export interface PlaidItemInfo {
  docId: string;
  itemId: string;
  hasAccessToken: boolean;
  institutionName?: string;
}

export interface QueryUserResult {
  userId: string | null;
  agentId: string | null;
  authUserId: string | null;
  email: string;
  collections: CollectionCount[];
  plaidItems: PlaidItemInfo[];
  totalDocuments: number;
  hasAuthOnly: boolean;
}

export interface DeletionProgress {
  collection: string;
  success: number;
  failed: number;
  error?: string;
}

export interface PlaidDeletionResult {
  itemId: string;
  success: boolean;
  error?: string;
}

export interface DeleteUserResult {
  success: boolean;
  userId: string | null;
  agentId: string | null;
  authUserId: string | null;
  deletionResults: DeletionProgress[];
  plaidDeletionResults: PlaidDeletionResult[];
  authDeletionResult: { success: boolean; error?: string; manualRemovalRequired?: boolean };
  totalDeleted: number;
  totalFailed: number;
}

interface DeleteUserRequest {
  email: string;
  mode: 'query' | 'delete';
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Query user data by email (returns counts without deleting)
 * Super_agent only
 * @param email - The email address to search for
 * @returns QueryUserResult with collection counts and Plaid items
 */
export const queryUserByEmail = async (email: string): Promise<QueryUserResult> => {
  try {
    const fn = httpsCallable<DeleteUserRequest, QueryUserResult>(
      functions,
      'deleteUserByEmail'
    );
    const result = await fn({ email, mode: 'query' });
    return result.data;
  } catch (error: any) {
    console.error('Error querying user data:', error);
    // Extract the actual error message from Firebase
    const message = error?.message || error?.code || 'Failed to query user data';
    throw new Error(message);
  }
};

/**
 * Delete user data by email (permanently deletes all data)
 * Super_agent only
 * @param email - The email address to delete
 * @returns DeleteUserResult with success/fail counts per collection
 */
export const deleteUserByEmail = async (email: string): Promise<DeleteUserResult> => {
  try {
    const fn = httpsCallable<DeleteUserRequest, DeleteUserResult>(
      functions,
      'deleteUserByEmail'
    );
    const result = await fn({ email, mode: 'delete' });
    return result.data;
  } catch (error: any) {
    console.error('Error deleting user data:', error);
    // Extract the actual error message from Firebase
    const message = error?.message || error?.code || 'Failed to delete user data';
    throw new Error(message);
  }
};
